import { Icon } from '@common/components/Icon';
import { Palette } from '@common/theming/Palette';
import { Wrapper } from '@googlemaps/react-wrapper';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ImageCarousel } from 'src/apps/portfolio/components/ImageCarousel';
import { ImageGallery } from '../components/ImageGallery';
import { APP_CONTENT_BLOCKS } from '../constants';
import About from '../elements/App/About';
import AssetDetails from '../elements/App/AssetDetails';
import BusinessPlan from '../elements/App/BusinessPlan';
import CapitalStack from '../elements/App/CapitalStack';
import Features from '../elements/App/Features';
import Header from '../elements/App/Header';
import InvestmentOverview from '../elements/App/InvestmentOverview';
import LoanDetails from '../elements/App/LoanDetails';
import LocationInfo from '../elements/App/LocationInfo';
import Map from '../elements/App/Map';
import PrimaryPartner from '../elements/App/PrimaryPartner';
import ProfileAssetHeading from '../elements/App/ProfileAssetHeading';
import PropertyValuation from '../elements/App/PropertyValuation';
import RiskMitigants from '../elements/App/RiskMitigants';
import Timeline from '../elements/App/Timeline';
import TopEmployers from '../elements/App/TopEmployers';
import { TextBlock } from '../elements/TextBlock';
import { InjectedWindowContext, PortfolioAsset } from '../types';

export type AssetState = 'LOADING' | 'EMPTY' | 'IMAGE_GALLERY' | 'SHOW_LARGE_MAP' | 'RENDER';

export type AssetBlockProps = { asset: PortfolioAsset };

export const MAX_PAGE_WIDTH = 540;

const styles: Record<string, React.CSSProperties> = {
  assetBlock: {
    paddingLeft: 24,
    paddingRight: 24,
  },
};

const Spacer = () => (
  <div
    style={{
      borderTopColor: Palette.WHITE_05,
      borderTopStyle: 'solid',
      borderTopWidth: 1,
      marginBottom: 40,
      marginTop: 40,
    }}></div>
);

type AssetAppProps = { asset: PortfolioAsset };

export const AssetApp: React.FC<AssetAppProps> = ({ asset }) => {
  const [currentState, setCurrentState] = useState<AssetState>('RENDER');
  const [scrollToCarousel, setScrollToCarousel] = useState<boolean>(false);
  const [currentSlide, setCurrentSlide] = useState<number>(0);
  const { assetId } = useParams();

  const LargeMap = ({ center, zoom }: { center: google.maps.LatLngLiteral; zoom: number }) => {
    const ref = useRef<HTMLElement>();

    useEffect(() => {
      if (!ref.current) return;
      const map = new window.google.maps.Map(ref.current, {
        center,
        mapTypeControl: false,
        streetViewControl: false,
        zoom,
      });
      new google.maps.Marker({
        map: map,
        position: new google.maps.LatLng(
          asset.mapInformation?.location.lat || 0,
          asset.mapInformation?.location.long || 0,
        ),
      });
    });

    return (
      <div
        style={{ height: '100%', padding: 25, width: '100%' }}
        ref={ref as React.MutableRefObject<HTMLDivElement>}
        id="map"
      />
    );
  };

  useEffect(() => {
    document.body.style.webkitUserSelect = 'none';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    if (currentState === 'RENDER') {
      if (scrollToCarousel) {
        carouselRef.current?.scrollIntoView();
        setScrollToCarousel(false);
      }
    }
  }, [currentState]);

  const carouselRef = useRef<HTMLDivElement>(null);

  const windowContext: InjectedWindowContext = window;

  const getAssetBlock = (key: keyof PortfolioAsset, reactKey: string) => {
    let element;
    if (!asset || !asset[key]) return;

    switch (key) {
      case 'assetDetails':
        element = (
          <div style={styles.assetBlock}>
            <Spacer />
            <AssetDetails asset={asset} />
          </div>
        );
        break;
      case 'assetFeatures':
        element = (
          <div style={styles.assetBlock}>
            <Spacer />
            <Features asset={asset} />
          </div>
        );
        break;
      case 'businessPlan':
        element = (
          <div style={styles.assetBlock}>
            <Spacer />
            <BusinessPlan asset={asset} />
          </div>
        );
        break;
      case 'capitalStack':
        element = (
          <div style={styles.assetBlock}>
            <Spacer />
            <CapitalStack asset={asset} />
          </div>
        );
        break;
      case 'description':
        element = (
          <div style={styles.assetBlock}>
            <Spacer />
            <About asset={asset} />
          </div>
        );
        break;
      case 'investmentOverview':
        element = (
          <div style={styles.assetBlock}>
            <Spacer />
            <InvestmentOverview asset={asset} />
          </div>
        );
        break;
      case 'locationInfo':
        element = (
          <div style={styles.assetBlock}>
            <Spacer />
            <LocationInfo asset={asset} />
          </div>
        );
        break;
      case 'loanDetails':
        element = (
          <div style={styles.assetBlock}>
            <Spacer />
            <LoanDetails asset={asset} />
          </div>
        );
        break;
      case 'mapInformation':
        element = (
          <div style={styles.assetBlock}>
            <Map
              asset={asset}
              showMap={() => {
                windowContext.ReactNativeWebView?.postMessage('HIDE_EXIT_BUTTON');
                setCurrentState('SHOW_LARGE_MAP');
              }}
            />
          </div>
        );
        break;
      case 'primaryPartner':
        element = (
          <div style={styles.assetBlock}>
            <Spacer />
            <PrimaryPartner asset={asset} />
          </div>
        );
        break;
      case 'propertyValuation':
        element = (
          <div style={styles.assetBlock}>
            <Spacer />
            <PropertyValuation asset={asset} />
          </div>
        );
        break;
      case 'risksAndMitigants':
        element = (
          <div style={styles.assetBlock}>
            <Spacer />
            <RiskMitigants asset={asset} />
          </div>
        );
        break;
      case 'timeLine':
        element = (
          <div style={styles.assetBlock}>
            <Spacer />
            <Timeline asset={asset} />
          </div>
        );
        break;
      case 'topEmployers':
        element = (
          <div style={styles.assetBlock}>
            <Spacer />
            <TopEmployers asset={asset} />
          </div>
        );
        break;
      case 'images':
        element =
          asset.images.length > 1 ? (
            <div ref={carouselRef}>
              <div style={{ paddingLeft: 24, paddingRight: 24 }}>
                <Spacer />
              </div>
              <div style={{ paddingLeft: 24 }}>
                <ProfileAssetHeading heading={`Explore ${asset.header.title}`} />
              </div>
              <div style={{ marginTop: 24 }}>
                <ImageCarousel
                  maxImageWidth={MAX_PAGE_WIDTH}
                  images={asset.images.map((image) => image.file.url)}
                  initial={1}
                  onImageClick={(slide) => {
                    windowContext.ReactNativeWebView?.postMessage('HIDE_EXIT_BUTTON');
                    setScrollToCarousel(true);
                    setCurrentSlide(slide);
                    setCurrentState('IMAGE_GALLERY');
                  }}
                />
              </div>
            </div>
          ) : null;
        break;
    }
    return <div key={reactKey}>{element} </div>;
  };

  let content;
  switch (currentState) {
    case 'LOADING':
      content = <div>Loading</div>;
      break;
    case 'EMPTY':
      content = (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 34 }}>
          <TextBlock size={16} color={Palette.WHITE_70}>
            Sorry! Couldn't find Asset {assetId}
          </TextBlock>
        </div>
      );
      break;
    case 'RENDER':
      content = asset ? (
        <div style={{ maxWidth: MAX_PAGE_WIDTH, overflow: 'hidden', paddingBottom: 64 }}>
          <div>
            <Header asset={asset} setCurrentState={setCurrentState} />
          </div>
          {APP_CONTENT_BLOCKS.map((assetKey, i) => getAssetBlock(assetKey as keyof PortfolioAsset, `block-${i}`))}
        </div>
      ) : null;
      break;
    case 'IMAGE_GALLERY':
      content = (
        <div style={{ height: '100vh', maxWidth: MAX_PAGE_WIDTH, paddingTop: 45 }}>
          <div style={{ alignItems: 'center', display: 'flex', height: 50, justifyContent: 'space-between' }}>
            <div style={{ height: 50, width: 50 }} />
            <TextBlock size={16}>{`${asset?.header.title} ${currentSlide + 1}/${asset.images.length}`}</TextBlock>
            <div
              onClick={() => {
                windowContext.ReactNativeWebView?.postMessage('SHOW_EXIT_BUTTON');
                setCurrentState('RENDER');
              }}
              style={{
                alignItems: 'center',
                display: 'flex',
                height: 50,
                justifyContent: 'flex-end',
                marginRight: 8,
                width: 50,
              }}>
              <Icon icon="times" boxSize={'20px'} color="white" />
            </div>
          </div>
          <div style={{ marginTop: -50, position: 'relative', top: '50%', transform: 'translateY(-50%)' }}>
            <ImageGallery
              initial={currentSlide}
              onSlideChange={(slide) => {
                setCurrentSlide(slide);
              }}
              images={asset ? asset?.images.map((image) => image.file.url) : []}
            />
          </div>
          ;
        </div>
      );
      break;
    case 'SHOW_LARGE_MAP':
      content = (
        <div style={{ height: '100vh', maxWidth: '100vw' }}>
          <div
            onClick={() => {
              windowContext.ReactNativeWebView?.postMessage('SHOW_EXIT_BUTTON');
              setCurrentState('RENDER');
            }}
            style={{
              alignItems: 'center',
              backgroundColor: Palette.WHITE,
              borderRadius: 12,
              boxShadow: '0px 0px 3px 1px #999999',
              display: 'flex',
              height: 24,
              justifyContent: 'center',
              left: 24,
              position: 'absolute',
              top: 60,
              width: 24,
              zIndex: 2,
            }}>
            <Icon icon="times" boxSize={'12px'} color="black" />
          </div>

          <Wrapper apiKey={'AIzaSyAZKMoXSNlcFPxTCT3nVY5GsOBItH_OHTg'}>
            <LargeMap
              center={{ lat: asset.mapInformation?.location.lat || 0, lng: asset.mapInformation?.location.long || 0 }}
              zoom={16}
            />
          </Wrapper>
        </div>
      );
  }

  return <div style={{ backgroundColor: Palette.BLUE_GRAY, flex: 1 }}>{content}</div>;
};
